import React from 'react';
import { motion } from 'framer-motion';
interface AnalogClockProps {
  hours: number;
  minutes: number;
  seconds?: number;
  size?: number;
  showSeconds?: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
  className?: string;
}
export function AnalogClock({
  hours,
  minutes,
  seconds = 0,
  size: propSize,
  showSeconds = true,
  label,
  mode = 'support',
  className = ''
}: AnalogClockProps) {
  // Default size based on mode if not explicitly provided
  const size = propSize ?? (mode === 'stimulus' ? 120 : 160);
  // Calculate angles
  const hourAngle = hours % 12 * 30 + minutes / 60 * 30;
  const minuteAngle = minutes * 6 + seconds / 60 * 6;
  const secondAngle = seconds * 6;
  return <motion.div initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.35,
    ease: [0.22, 1, 0.36, 1]
  }} className="flex flex-col items-center gap-3" data-id="element-8998">
      <div className={`relative rounded-full bg-gradient-to-br from-white to-slate-50 shadow-2xl border-8 border-slate-100 ${className}`} style={{
      width: size,
      height: size
    }} data-id="element-8999">
        {/* Outer ring decoration */}
        <div className="absolute inset-0 rounded-full border-4 border-slate-200/50" data-id="element-9000" />

        {/* Clock Face Numbers */}
        {[...Array(12)].map((_, i) => {
        const num = i + 1;
        const rotation = num * 30;
        // Position numbers in a circle
        const radius = size / 2 - 30;
        const angleInRadians = (rotation - 90) * (Math.PI / 180);
        const x = size / 2 + radius * Math.cos(angleInRadians);
        const y = size / 2 + radius * Math.sin(angleInRadians);
        return <div key={num} className="absolute flex items-center justify-center font-black text-slate-700" style={{
          width: 28,
          height: 28,
          left: x - 14,
          top: y - 14,
          fontSize: size > 150 ? '1.2rem' : '0.9rem'
        }} data-id="element-9001">
              {num}
            </div>;
      })}

        {/* Hour Marks (bold) */}
        {[...Array(12)].map((_, i) => {
        const rotation = i * 30;
        return <div key={`hour-mark-${i}`} className="absolute bg-slate-400 origin-bottom rounded-full" style={{
          width: 3,
          height: 10,
          left: 'calc(50% - 1.5px)',
          top: 8,
          transformOrigin: `50% ${size / 2 - 8}px`,
          transform: `rotate(${rotation}deg)`
        }} data-id="element-9002" />;
      })}

        {/* Minute Marks */}
        {[...Array(60)].map((_, i) => {
        if (i % 5 === 0) return null; // Skip where hour marks are
        const rotation = i * 6;
        return <div key={`mark-${i}`} className="absolute bg-slate-300 origin-bottom" style={{
          width: 1.5,
          height: 6,
          left: 'calc(50% - 0.75px)',
          top: 12,
          transformOrigin: `50% ${size / 2 - 12}px`,
          transform: `rotate(${rotation}deg)`
        }} data-id="element-9003" />;
      })}

        {/* Hour Hand - FIXED: Better positioning */}
        <motion.div className="absolute origin-bottom z-10 rounded-full" style={{
        width: 8,
        height: size * 0.28,
        left: 'calc(50% - 4px)',
        bottom: '50%',
        borderRadius: '6px 6px 0 0',
        background: 'linear-gradient(to bottom, #1e293b 0%, #334155 100%)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        transformOrigin: 'center bottom'
      }} animate={{
        rotate: hourAngle
      }} transition={{
        type: 'spring',
        stiffness: 100,
        damping: 15
      }} data-id="element-9004" />

        {/* Minute Hand - FIXED: Better positioning */}
        <motion.div className="absolute origin-bottom z-20 rounded-full" style={{
        width: 6,
        height: size * 0.38,
        left: 'calc(50% - 3px)',
        bottom: '50%',
        borderRadius: '5px 5px 0 0',
        background: 'linear-gradient(to bottom, #475569 0%, #64748b 100%)',
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        transformOrigin: 'center bottom'
      }} animate={{
        rotate: minuteAngle
      }} transition={{
        type: 'spring',
        stiffness: 100,
        damping: 15
      }} data-id="element-9005" />

        {/* Second Hand - FIXED: Better positioning */}
        {showSeconds && <motion.div className="absolute origin-bottom z-30" style={{
        width: 2,
        height: size * 0.42,
        left: 'calc(50% - 1px)',
        bottom: '50%',
        borderRadius: '2px 2px 0 0',
        background: 'linear-gradient(to bottom, #ef4444 0%, #dc2626 100%)',
        boxShadow: '0 1px 4px rgba(239,68,68,0.3)',
        transformOrigin: 'center bottom'
      }} animate={{
        rotate: secondAngle
      }} transition={{
        type: 'spring',
        stiffness: 150,
        damping: 10
      }} data-id="element-9006" />}

        {/* Center Dot - Layered for depth */}
        <div className="absolute rounded-full z-40" style={{
        width: 16,
        height: 16,
        left: 'calc(50% - 8px)',
        top: 'calc(50% - 8px)',
        background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
        border: '3px solid white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
      }} data-id="element-9007" />

        {showSeconds && <div className="absolute bg-red-500 rounded-full z-50" style={{
        width: 8,
        height: 8,
        left: 'calc(50% - 4px)',
        top: 'calc(50% - 4px)',
        boxShadow: '0 1px 3px rgba(239,68,68,0.4)'
      }} data-id="element-9008" />}
      </div>

      {label && <p className={`font-semibold text-slate-600 text-center ${mode === 'stimulus' ? 'text-xs' : 'text-sm'}`} data-id="element-9009">
          {label}
        </p>}
    </motion.div>;
}